import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Alert, Tooltip } from 'antd';
import DataTable from '@/components/data-table';
import InvoiceStatus from '@/components/invoice/InvoiceStatus';
import { API } from '@/services/api.service';
import { balanceText, date, money } from './format';

const NOTICE = {
  noPib: 'unbooked.noPib',
  noProject: 'unbooked.noProject',
  noPartner: 'unbooked.noPartner',
  notTracked: 'unbooked.notTracked',
};

/**
 * Why the books do not hold an invoice yet — our roles only. Reuses the
 * invoice table's own hints where they exist.
 */
export function ReasonText({ reason }) {
  const { t } = useTranslation(['ledgerCards', 'tables']);
  const key = {
    missing: 'postedToErpMissingHint',
    supplier: 'postedToErpUncheckedSupplier',
    year: 'postedToErpUncheckedYear',
  }[reason];
  const label = key ? t(key, { ns: 'tables' }) : t(`unbooked.reasons.${reason}`, { defaultValue: reason });
  return (
    <Tooltip title={label} placement="topLeft">
      <span>{label}</span>
    </Tooltip>
  );
}

/** Book balance, in progress, balance including them — one row each, the last one bold. */
function Expected({ rows }) {
  return (
    <dl className="grid w-full max-w-[520px] grid-cols-[1fr_auto] gap-x-6 gap-y-0.5 rounded-lg border border-zinc-200 bg-white p-3 text-[13px]">
      {rows.map(([label, value, strong]) => (
        <div key={label} className="contents">
          <dt className={strong ? 'border-t border-zinc-100 pt-0.5 font-semibold' : 'text-zinc-500'}>{label}</dt>
          <dd className={`m-0 text-right tabular-nums ${strong ? 'border-t border-zinc-100 pt-0.5 font-semibold' : ''}`}>
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * „Received, not yet booked invoices" under a card: the incoming invoices on
 * the platform for the card's supplier (by PIB) or project that the books do
 * not hold yet, paged on the server, and the expected balance — the card's
 * total plus what will be booked. The totals come with the card; the rows
 * page on their own, so their page lives in `uOffset` / `uLimit`.
 *
 * @param {Object} props
 * @param {string} props.company
 * @param {Object} props.card - the card from the server (with `unbooked`)
 * @param {Object} props.filters - the card's { from, to, account?, partner?, orgUnit? }
 * @param {Object} props.queryParams
 * @param {Function} props.setQueryParams
 */
export default function UnbookedInvoices({ company, card, filters, queryParams, setQueryParams }) {
  const { t } = useTranslation('ledgerCards');
  const summary = card?.unbooked;
  const page = { offset: String(queryParams.uOffset || '1'), limit: String(queryParams.uLimit || '10') };

  const { data, isLoading, error } = useQuery({
    queryKey: ['ledger-cards', company, 'unbooked', filters, page],
    queryFn: () => API.ledgerCards.getUnbooked(company, { ...filters, ...page }),
    enabled: Boolean(company && summary?.available && summary.count),
    placeholderData: keepPreviousData,
    retry: false,
  });

  // A card on an account outside 43*: a supplier's invoices never land there.
  if (!summary || summary.reason === 'notSupplierAccount') return null;
  const balanceShown = Boolean(summary.balanceShown && summary.expected);
  const title = `${t('unbooked.title')}${summary.available ? ` · ${summary.count}` : ''}`;
  const side = (block) => balanceText(block, t);

  const columns = [
    { title: t('unbooked.number'), dataIndex: 'documentNumber', key: 'documentNumber', width: 170, ellipsis: true },
    { title: t('unbooked.date'), dataIndex: 'documentDate', key: 'documentDate', width: 110, render: date },
    { title: t('unbooked.due'), dataIndex: 'dueDate', key: 'dueDate', width: 110, render: date },
    !filters.partner && {
      title: t('unbooked.supplier'),
      dataIndex: 'supplierName',
      key: 'supplierName',
      width: 240,
      ellipsis: { showTitle: false },
      render: (v, row) => (
        <Tooltip title={row.supplierVat ? `${t('head.pib')} ${row.supplierVat}` : ''} placement="topLeft">
          <span>{v}</span>
        </Tooltip>
      ),
    },
    {
      title: t('unbooked.project'),
      dataIndex: 'project',
      key: 'project',
      width: 200,
      ellipsis: true,
      render: (p) => (p ? [p.code, p.name].filter(Boolean).join(' ') : ''),
    },
    {
      title: t('unbooked.gross'),
      dataIndex: 'gross',
      key: 'gross',
      width: 150,
      align: 'right',
      // A credit note lowers what is owed: shown negative.
      render: (_, row) => money(row.debit ? -row.debit : row.credit),
    },
    { title: t('unbooked.currency'), dataIndex: 'currency', key: 'currency', width: 80 },
    {
      title: t('unbooked.status'),
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (s) =>
        s?.key ? (
          <InvoiceStatus
            status={{ ...s, label: t(`unbooked.statuses.${s.key}`, { defaultValue: s.key }) }}
          />
        ) : null,
    },
    // Ours only: the server sends these fields to nobody else.
    data?.internal && {
      title: t('unbooked.days'),
      dataIndex: 'daysSinceReceipt',
      key: 'daysSinceReceipt',
      width: 110,
      align: 'right',
    },
    data?.internal && {
      title: t('unbooked.reason'),
      dataIndex: 'reason',
      key: 'reason',
      width: 260,
      ellipsis: { showTitle: false },
      render: (r) => (r ? <ReasonText reason={r} /> : null),
    },
  ].filter(Boolean);

  const expectedRows = balanceShown
    ? [
        [t('unbooked.bookBalance'), side(card.total)],
        [t('unbooked.total', { count: summary.rsd.count }), side(summary.rsd)],
        [t('unbooked.expected'), side(summary.expected), true],
        ...summary.expectedFx.flatMap((fx) => {
          const own = summary.byCurrency.find((g) => g.currency === fx.currency);
          return [
            [`${t('unbooked.bookBalance')} (${fx.currency})`, side(fx.book)],
            [`${t('unbooked.total', { count: own?.count || 0 })} (${fx.currency})`, side(own)],
            [`${t('unbooked.expected')} (${fx.currency})`, side(fx.expected), true],
          ];
        }),
      ]
    : [];

  return (
    <div className="grid gap-3">
      {!summary.available && (
        <Alert type="info" showIcon message={t('unbooked.title')} description={t(NOTICE[summary.reason] || NOTICE.notTracked)} />
      )}
      {summary.available && (
        <>
          <p className="m-0 text-[13px] text-zinc-500">{t('unbooked.hint')}</p>
          {balanceShown && <Expected rows={expectedRows} />}
          {balanceShown && summary.expectedFx.length > 0 &&<p className="m-0 text-[12px] text-zinc-500">{t('unbooked.fxNote')}</p>}
          {summary.count === 0 ? (
            <Alert type="info" showIcon message={t('unbooked.none')} />
          ) : (
            <DataTable
              title={title}
              data={data?.items || []}
              total={data?.count || 0}
              status={isLoading ? 'pending' : error ? 'error' : 'success'}
              columns={columns}
              offset={page.offset}
              limit={page.limit}
              syncUrl={false}
              onChangePagination={(key, value) =>
                setQueryParams(key === 'limit' ? { uLimit: value, uOffset: 1 } : { uOffset: value })
              }
              tableLayout="fixed"
              scroll={{ x: columns.reduce((s, c) => s + (c.width || 0), 0) }}
              size="small"
            />
          )}
        </>
      )}
    </div>
  );
}
